var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '@cb/apricot/CBTable';
import cn from 'classnames';
import { isNil, orderBy } from 'lodash';
import { useUID } from 'react-uid';
import flattenChildren from 'react-flatten-children';
import ResizeDetector from 'react-resize-detector';
import { LoadingWrapper } from './LoadingWrapper';
import TableHeader from './TableHeader';
import { EXPANDER_RIGHT, EXPANDER_NONE, SORT_DEFAULT, } from './types';
import { TableRow } from './TableRow';
import { useOnScreen } from './useOnScreen';
import { TableHeaderContext } from './context';
export const Table = (_a) => {
    var { tableId, children, data, condensed, borderless, borderlessColumn, striped, headless, light, initialSort, onSort, sortFn, isExternalSort = false, noStretch, responsive, tabIndex, topScroll, forceResize = false, className, columnTitleSeparator, a11yHighlightType = 'append', a11yHighlightText = 'highlighted cell', a11ySelectedText = 'selected cell', summary, sortType = SORT_DEFAULT, expandableRows, 
    // todo: why there is no handler that uses expandOnRowClick?
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    expandOnRowClick, expanderPosition = EXPANDER_RIGHT, stickyHeader, stickyHeaderParent, stickyColumn, overflowX, positionRelative, maxHeight, rowClassNameFunc, rowKeyFunc, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rowAttributes, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rowAttributesFunc, forceColumnsUpdate } = _a, rowProps = __rest(_a, ["tableId", "children", "data", "condensed", "borderless", "borderlessColumn", "striped", "headless", "light", "initialSort", "onSort", "sortFn", "isExternalSort", "noStretch", "responsive", "tabIndex", "topScroll", "forceResize", "className", "columnTitleSeparator", "a11yHighlightType", "a11yHighlightText", "a11ySelectedText", "summary", "sortType", "expandableRows", "expandOnRowClick", "expanderPosition", "stickyHeader", "stickyHeaderParent", "stickyColumn", "overflowX", "positionRelative", "maxHeight", "rowClassNameFunc", "rowKeyFunc", "rowAttributes", "rowAttributesFunc", "forceColumnsUpdate"]);
    const [sortState, setSortState] = useState(initialSort || {});
    const [loading, setLoading] = useState(false);
    const [scrollIsOn, setScrollIsOn] = useState(false);
    const [tableVisibleWidth, setTableVisibleWidth] = useState(0);
    const [tableWidth, setTableWidth] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [focusedField, setFocusedField] = useState(null);
    const tableRef = useRef(null);
    const wrapperRef = useRef(null);
    const topScrollRef = useRef(null);
    const headerTableRef = useRef(null);
    const headerWrapperRef = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    tableId = tableId || `apricot_table_${uid}`;
    const tblKey = `key_tbl_${tableId}`;
    const wrapperKey = `key_wrapper_${tableId}`;
    const [sortedData, setSortedData] = useState([]);
    /** Build a list of columns from children */
    const getColumns = () => flattenChildren(children).filter(child => !!child);
    /** Hold the updated list of flattened children whenever they are updated or forced to be updated */
    const [flatColumns, setFlatColumns] = useState(getColumns());
    /** If children change and forceColumnsUpdate is not set - skip the update */
    useEffect(() => {
        if (!forceColumnsUpdate) {
            setFlatColumns(getColumns());
        }
    }, [children]);
    /** if forceChickenUpdate changes, and it evaluates as JS `true` - force update the columns */
    useEffect(() => {
        if (forceColumnsUpdate) {
            setFlatColumns(getColumns());
        }
    }, [forceColumnsUpdate]);
    /**
     * Get sorted data if sort state is defined
     */
    const getSortedData = () => {
        return sortFn
            ? sortFn(data, sortState)
            : orderBy(data, [sortState.field], [sortState.ascending ? 'asc' : 'desc']);
    };
    useEffect(() => {
        setSortedData(sortState && !isExternalSort ? getSortedData() : data);
    }, [sortState, data]);
    const resizeColumnWidths = () => {
        if (headerTableRef.current) {
            const mainTableCells = tableRef.current.querySelectorAll('tr th');
            headerTableRef.current
                .querySelectorAll('tr th')
                .forEach((el, index) => {
                // eslint-disable-next-line no-param-reassign
                el.style.width = `${mainTableCells[index].getBoundingClientRect().width}px`;
            });
            setHeaderHeight(headerTableRef.current.getBoundingClientRect().height);
        }
    };
    /** recalculate columns on major table change */
    useEffect(() => {
        if (stickyHeader) {
            setTimeout(() => {
                resizeColumnWidths();
            }, 300);
        }
    }, [sortedData, stickyHeader, expandableRows]);
    // store rowSpans for grouped fields in an array of numbers
    const rowSpans = {};
    // store the previous row indexes for grouped fields as numbers
    const prevRowIndexes = {};
    // loop through all the rows and calculate rowSpans
    sortedData.forEach((row, rowIndex) => {
        // pick only columns with groupValues === true
        flatColumns.forEach(({ props: { groupValues, field } }) => {
            if (groupValues) {
                if (!rowSpans[field]) {
                    rowSpans[field] = new Array(sortedData.length);
                }
                const prevRowIndex = prevRowIndexes[field];
                // if the current cell value is the same - increment the prev rowSpan and set the current to 0
                if (!isNil(prevRowIndex) && sortedData[prevRowIndex][field] === row[field]) {
                    rowSpans[field][rowIndex] = 0;
                    rowSpans[field][prevRowIndex] += 1;
                }
                else {
                    // if this cell does not match the previous - save it as the previous reference
                    rowSpans[field][rowIndex] = 1;
                    prevRowIndexes[field] = rowIndex;
                }
            }
        });
    });
    const tableClassName = cn('cb-table', 'cb-table-react', light && 'cb-table-light', borderlessColumn && 'cb-no-border-left-right', condensed && 'cb-table-condensed', borderless && 'cb-no-border', striped && 'cb-table-striped-reverse', expandableRows && 'cb-table-expandable', stickyColumn && 'cb-sticky-table-column', noStretch && 'cb-table-no-stretch', className);
    /**
     * Top Scroll if available - sync the table scroll
     */
    const topScrollEventHandler = () => {
        if (topScroll && wrapperRef.current && topScrollRef.current) {
            wrapperRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };
    /**
     * Wrapper Scroll event handler - sync top scroll
     */
    const wrapperScrollEventListener = () => {
        if (wrapperRef.current) {
            if (topScrollRef.current) {
                topScrollRef.current.scrollLeft = wrapperRef.current.scrollLeft;
            }
            headerWrapperRef.current.scrollLeft = wrapperRef.current.scrollLeft;
        }
    };
    /**
     * Window Resize event handler - save the current widths
     */
    const resizeEventHandler = () => {
        tableRef.current && setTableWidth(tableRef.current.offsetWidth);
        if (wrapperRef.current) {
            setTableVisibleWidth(wrapperRef.current.parentElement.offsetWidth);
        }
    };
    /**
     * Control Mount - add window resize event listeners
     */
    useEffect(() => {
        resizeEventHandler();
        window.addEventListener('resize', resizeEventHandler);
        // remove the event on dismount
        return () => {
            window.removeEventListener('resize', resizeEventHandler);
        };
    }, []);
    /**
     * Recalculate the width of the table and parent
     */
    useEffect(() => {
        if (typeof forceResize === 'boolean') {
            forceResize && resizeEventHandler();
        }
        else {
            resizeEventHandler();
        }
    }, [forceResize]);
    /**
     * Children change event - handle as a regular resize with a timeout
     */
    useEffect(() => {
        setTimeout(() => {
            resizeEventHandler();
        }, 100);
    }, [flatColumns]);
    useEffect(() => {
        if (wrapperRef.current) {
            wrapperRef.current.addEventListener('scroll', wrapperScrollEventListener);
            wrapperScrollEventListener();
        }
    }, [topScroll, wrapperRef.current]);
    /**
     * TopScroll is shown - add event handlers
     */
    useEffect(() => {
        let scrollRef = null;
        if (topScrollRef.current) {
            topScrollRef.current.addEventListener('scroll', topScrollEventHandler);
            scrollRef = topScrollRef.current;
        }
        return () => {
            if (scrollRef) {
                scrollRef.removeEventListener('scroll', topScrollEventHandler);
            }
        };
    }, [topScroll, scrollIsOn]);
    /**
     * Widths are changed - decide if the scroll is shown
     */
    useEffect(() => {
        setScrollIsOn(tableVisibleWidth < tableWidth);
        headerTableRef.current &&
            (headerTableRef.current.style.width = `${tableRef.current.getBoundingClientRect().width}px`);
        headerWrapperRef.current &&
            (headerWrapperRef.current.style.width = `${wrapperRef.current.getBoundingClientRect().width}px`);
    }, [tableVisibleWidth, tableWidth]);
    const { isTopAbove, isVisible } = useOnScreen(tableRef, `-${headerHeight}px` /* "-200px" */);
    const onSortClick = (field) => __awaiter(void 0, void 0, void 0, function* () {
        if (onSort) {
            setLoading(true);
            yield onSort(field, field !== sortState.field || !sortState.ascending);
            setLoading(false);
        }
        setSortState({
            field,
            ascending: field !== sortState.field || !sortState.ascending,
        });
    });
    // Context for the stickyMode header
    const stickyModeContext = useMemo(() => ({ stickyMode: true }), []);
    const headerTable = stickyHeader && !headless && (React.createElement("table", { key: `${tblKey}_h`, id: `${tableId}_hdr`, className: tableClassName, summary: summary, ref: headerTableRef, "aria-hidden": "true" },
        React.createElement(TableHeaderContext.Provider, { value: stickyModeContext },
            React.createElement(TableHeader, { expanderPosition: expandableRows ? expanderPosition : EXPANDER_NONE, columns: flatColumns, columnTitleSeparator: columnTitleSeparator, onSortClick: onSortClick, sortState: sortState, sortType: sortType, focusedFieldDispatch: [focusedField, setFocusedField] }))));
    const table = (React.createElement(React.Fragment, null,
        React.createElement(ResizeDetector, { onResize: resizeColumnWidths }),
        React.createElement("table", { key: tblKey, id: tableId, className: tableClassName, summary: summary, tabIndex: tabIndex, "data-cbtrack-comp": "apricot-react:table", ref: tableRef },
            !headless && (React.createElement(TableHeader, { columns: flatColumns, columnTitleSeparator: columnTitleSeparator, onSortClick: onSortClick, sortState: sortState, sortType: sortType, expanderPosition: expandableRows ? expanderPosition : EXPANDER_NONE, focusedFieldDispatch: [focusedField, setFocusedField], stickyHeaderParent: stickyHeaderParent })),
            React.createElement("tbody", null, sortedData.map((row, rowIndex) => {
                const isRowStriped = striped ? rowIndex % 2 === 1 : undefined;
                return (React.createElement(TableRow, Object.assign({ key: rowKeyFunc ? rowKeyFunc(row, rowIndex) : rowIndex, row: row, rowIndex: rowIndex, expanderPosition: expanderPosition, expandableRows: expandableRows, columns: flatColumns, rowSpans: rowSpans, a11yHighlightType: a11yHighlightType, a11yHighlightText: a11yHighlightText, a11ySelectedText: a11ySelectedText, isRowStriped: isRowStriped, rowAttributes: rowAttributes, rowClassNameFunc: rowClassNameFunc, rowAttributesFunc: rowAttributesFunc }, rowProps)));
            })))));
    return (React.createElement(LoadingWrapper, { loading: loading, positionRelative: positionRelative, overflowX: overflowX, maxHeight: maxHeight },
        React.createElement(React.Fragment, null,
            React.createElement("div", { className: cn('cb-table-react', 'cb-table-header', isVisible && isTopAbove ? 'cb-sticky-table' : ''), style: { width: topScroll ? tableVisibleWidth : undefined } },
                topScroll && scrollIsOn && (React.createElement("div", { className: "cb-table-top-scroll-holder", style: { width: tableVisibleWidth }, ref: topScrollRef },
                    React.createElement("div", { className: "cb-table-top-scroll", style: { width: `${tableWidth}px` } }))),
                React.createElement("div", { className: cn('cb-table-wrapper', responsive && 'cb-table-responsive'), ref: headerWrapperRef }, headerTable)),
            topScroll && scrollIsOn && React.createElement("div", { className: "cb-table-top-scroll-holder" }),
            React.createElement("div", { key: wrapperKey, className: cn('cb-table-wrapper', responsive && 'cb-table-responsive', topScroll && 'with-top-scroll'), ref: wrapperRef, style: { width: topScroll ? tableVisibleWidth : undefined } }, table))));
};
export const Column = () => null;
// eslint-disable-next-line @typescript-eslint/ban-types,@typescript-eslint/no-unused-vars
export const typedTableComponents = (data = []) => ({
    Table: Table,
    Column: Column,
});
export const attributesFromTupleArray = (attributes) => {
    return attributes.reduce((attrib, [aName, aValue]) => (Object.assign(Object.assign({}, attrib), { [aName]: aValue })), {});
};
//# sourceMappingURL=Table.js.map